<template>
	<main class="isolate">
		<PageHeaderGeneralSkeleton :title="title">
			<template #image>
				<NuxtPicture
					src="images/contact-us.jpg"
					:img-attrs="{class:'object-cover w-full h-full'}"
					class="absolute inset-0 z-10 pointer-events-none"
					sizes="sm:768px md:1280px lg:1920px"
					alt=""
				/>
			</template>
		</PageHeaderGeneralSkeleton>
		<section ref="sectionEl" class="relative container -mt-5 z-20 md:-mt-14">
			<template v-if="isSent">
				<div class="px-7 pt-4 pb-8 bg-background-secondary rounded-lg md:rem:max-w-[840px] md:mx-auto md:px-10 md:py-12 md:shadow-sm text-center rem:max-w-[614px] mx-auto">
					<div
						class="inline-flex justify-center items-center mb-10 bg-functional-success-secondary rounded-full rem:w-[100px] rem:h-[100px]"
					>
						<NuxtIcon name="check" filled class="text-6xl text-functional-success-primary" />
					</div>
					<h2 class="typo-h4 text-heading-secondary mb-6">
						Váš dotaz byl odeslán
					</h2>
					<AbsoluteInternalLink
						class="btn btn-primary btn-lg px-16 rem:min-w-[230px] justify-center"
						to="/"
					>
						Ok, rozumím
					</AbsoluteInternalLink>
				</div>
			</template>
			<FormKit
				v-else
				#default="{state: {valid}}"
				v-model="formData"
				type="form"
				form-class="px-7 pt-4 pb-8 bg-background-secondary rounded-lg md:rem:max-w-[840px] md:mx-auto md:px-10 md:py-12 md:shadow-sm"
				:actions="false"
				:incomplete-message="false"
				@submit="submit"
			>
				<div class="md:relative md:flex md:flex-col md:items-center md:[&>*]:rem:max-w-[338px] md:[&>*]:w-full">
					<h2 class="typo-h4 mb-3 md:absolute md:left-0 md:top-2">
						Vaše údaje
					</h2>
					<div v-if="profile" class="flex flex-wrap gap-x-10 gap-y-4 typo-body-s sm:grid sm:grid-cols-2 sm:gap-y-6">
						<div>
							<div class="mb-2 text-body-tertiary">
								Jméno
							</div>
							<div v-text="profile.personalInfo?.name" />
						</div>
						<div>
							<div class="mb-2 text-body-tertiary">
								Příjmení
							</div>
							<div v-text="profile.personalInfo?.surname" />
						</div>
						<div class="w-full">
							<div class="mb-2 text-body-tertiary">
								E-mail
							</div>
							<div v-text="profile.contacts?.email" />
						</div>
						<div class="w-full">
							<div class="mb-2 text-body-tertiary">
								Tel. číslo
							</div>
							<div v-text="profile.contacts?.phone" />
						</div>
						<div class="w-full">
							<div class="mb-2 text-body-tertiary">
								Číslo EAN:
							</div>
							<div v-text="profile.account?.card?.eanGlobusCard" />
						</div>
					</div>
					<div v-else>
						<div class="space-y-4 md:space-y-6">
							<FormKit
								type="text"
								label="Jméno*"
								name="name"
								placeholder=""
								:classes="{inner: '!max-w-full', input: 'py-[.6875rem]'}"
								validation="required|length:3"
								:validation-messages="{
									required: 'Jméno je povinné',
									length: 'Jméno musí obsahovat alespoň 3 znaky',
								}"
							/>
							<FormKit
								type="text"
								label="Příjmení*"
								name="surname"
								placeholder=""
								:classes="{inner: '!max-w-full', input: 'py-[.6875rem]'}"
								validation="required|length:3"
								:validation-messages="{
									required: 'Příjmení je povinné',
									length: 'Příjmení musí obsahovat alespoň 3 znaky',
								}"
							/>
							<FormKit
								type="email"
								label="E-mail*"
								name="email"
								placeholder=""
								:classes="{inner: '!max-w-full', input: 'py-[.6875rem]'}"
								validation="required|emailCustomRule"
								:validation-messages="{
									required: 'Vyplňte email',
								}"
							/>
							<FormKit
								type="tel"
								label="Tel. číslo"
								name="phone"
								placeholder=""
								:classes="{inner: '!max-w-full', input: 'py-[.6875rem]'}"
							/>
							<FormKit
								type="number"
								label="Číslo EAN:"
								name="eanGlobusCard"
								placeholder=""
								:classes="{inner: '!max-w-full', input: 'py-[.6875rem]'}"
								validation="matches:/^[0-9]{13}$/"
								:validation-messages="{
									matches: 'Číslo EAN musí mít 13 znaků',
								}"
							/>
						</div>
					</div>
				</div>

				<hr class="max-md:opacity-0 my-6 border-border-secondary md:mt-8 md:mb-10" />

				<div class="md:relative">
					<h2 class="typo-h4 mb-3 md:absolute md:left-0 md:top-2">
						Váš dotaz
					</h2>
					<div class="space-y-4 md:space-y-6 md:flex md:flex-col md:items-center md:[&>*]:rem:max-w-[338px] md:[&>*]:w-full">
						<FormKit
							type="selectbox"
							label="Důvod dotazu*"
							name="reason"
							:options="reasonOptions"
							placeholder="Vyberte důvod dotazu"
							:classes="{input: 'py-[.6875rem]'}"
							validation="required"
							:validation-messages="{
								required: 'Důvod dotazu je povinný',
							}"
						/>
						<FormKit
							type="selectbox"
							label="Dotaz k pobočce"
							name="house"
							:options="hypermarketsGsoaId"
							placeholder="Vyberte pobočku"
							:classes="{input: 'py-[.6875rem]'}"
						/>
						<FormKit
							type="fileUpload"
							label="Nahrát soubor"
							name="files"
							:accept="acceptFileTypes"
							:max-files="maxUploadFiles"
							:max-file-size="maxUploadFileSizeInMb"
							validation="*validateMaxFiles:3|*validateMaxFileSize:5|validateAcceptFiles"
							validation-visibility="live"
							:validation-rules="{validateMaxFiles, validateMaxFileSize, validateAcceptFiles}"
							:validation-messages="{
								validateMaxFiles: `Nelze náhrát více jak ${maxUploadFiles} soubory.`,
								validateMaxFileSize: `Soubory nelze nahrát. Překročili jste maximální povolenou velikost příloh ${maxUploadFileSizeMb}.`,
								validateAcceptFiles: 'Tento formát nepodporujeme. Použijte tyto formáty: .png, .jpg, .svg, .pdf, .jpeg, .HEIC',
							}"
							multiple
						>
							<template #help>
								<span class="mt-2 typo-body-caption text-body-secondary">
									Vložte jen soubory ve formátech <span class="text-body-primary">.png, .jpg, .svg, .pdf, .jpeg, .HEIC</span>
								</span>
							</template>
						</FormKit>
						<FormKit
							type="textarea"
							label="Váš dotaz*"
							name="message"
							placeholder=""
							:classes="{inner: '!max-w-full', input: 'py-[.6875rem] max-h-48'}"
							validation="required|length:0,500"
							:validation-messages="{
								required: 'Dotaz je povinný',
								length: 'Dotaz může obsahovat maximálně 500 znaků',
							}"
						/>
						<FormKit
							type="checkbox"
							name="terms"
							:classes="{
								outer: '!mt-8 md:!max-w-full md:!mt-12',
								inner: 'self-start translate-y-[.125rem]',
								label: 'typo-body-caption',
								decorator: 'flex items-center justify-center'
							}"
							validation="accepted"
							validation-visibility="dirty"
							:value="false"
							:validation-messages="{
								accepted: 'Tento souhlas je povinný',
							}"
						>
							<template #label>
								<p class="typo-body-caption">
									Souhlasím, aby společnost Globus ČR, v.o.s. se sídlem Praha 9 - Čakovice, Kostelecká
									822/75, PSČ 196 00, IČO: 634 73 291 (dále jen „Globus"), jako správce osobních údajů
									shromažďovala a zpracovávala mé osobní údaje v rozsahu jméno, příjmení a telefonní
									číslo nebo e-mailová adresa, a to za účelem zpracování a zodpovězení mého dotazu.
									Tento souhlas uděluji na dobu do poskytnutí odpovědi na můj dotaz, nejdéle však na
									dobu 90 dní, resp. do doby jeho odvolání. Beru na vědomí, že mám právo udělený
									souhlas kdykoliv odvolat. Udělením tohoto souhlasu současně potvrzuji, že jsem se
									seznámil/seznámila se
									<NuxtLink :to="termUrl" target="_blank" class="underline">
										Zásadami ochrany osobních údajů
									</NuxtLink>
									a se všemi právy, které mi v souvislosti se zpracováním osobních údajů náleží.*
									<br /><br />
									Vámi výše poskytnuté osobní údaje budou zpracovány společností Globus ČR, v.o.s. se
									sídlem Praha 9 - Čakovice, Kostelecká 822/75, PSČ 196 00, IČO: 634 73 291 (dále jen
									„Globus"), jakožto správcem osobních údajů. Výše poskytnuté osobní údaje budou
									zpracovány za účelem zpracování a zodpovězení Vašeho dotazu, který je rovněž důvodem
									zakládajícím jejich zpracování. Osobní údaje pro výše uvedený účel zpracuje
									společnost Go Digital! Direct s.r.o., IČO 06239986, se sídlem Masarykovo nám. 2655,
									530 02 Pardubice , tel.
									<a :href="`tel:${tel.replace(/ /g,'')}`" @click="trackLinkClickEvent">
										{{ tel }}
									</a>,
									e-mail
									<NuxtLink :to="`mailto:${email}`" class="underline" @click="trackLinkClickEvent">
										{{ email }}
									</NuxtLink>.
									Další informace o Vašich právech zajišťujících spravedlivé a transparentní
									zpracování Vašich osobních údajů jsou uvedeny v
									<NuxtLink :to="termUrl" target="_blank" class="underline">
										Zásadách ochrany osobních údajů
									</NuxtLink>.
								</p>
							</template>
							<template #decoratorIcon>
								<NuxtIcon name="check" filled class="text-xl" />
							</template>
						</FormKit>
						<p class="text-center typo-body-caption">
							Pole označená hvězdičkou (*) jsou povinná.
						</p>
						<p v-if="error" class="text-center !mt-12 text-functional-error-primary">
							{{ errorMessage }}
						</p>
						<FormKit
							type="submit"
							:label="loading ? 'Odesílání' : 'Odeslat'"
							:disabled="loading || !valid"
							:classes="{outer: 'md:!max-w-full !mt-12', input: 'btn btn-primary btn-xl w-full px-16 justify-center md:w-fit md:rem:min-w-[420px] md:mx-auto'}"
						/>
					</div>
				</div>
			</FormKit>
		</section>
	</main>
</template>

<script setup lang="ts">
	import type {FormKitNode} from '@formkit/core';
	import {set} from '@vueuse/core';
	import {storeToRefs} from 'pinia';
	import slugify from 'slugify';
	import {filesize} from 'filesize';
	import {MailingService} from '~/openapi';
	import type {Mailing_ContactForm as contactForm} from '~/openapi';
	import {validateFileAccept} from '~/utils/files';
	import {useCustomerProfileStore} from '~/stores/customerProfile';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {email, tel} from '~/resources';
	import {trackLinkClickEvent} from '~/utils/tracking';

	const title = 'Napište nám';
	const termUrl = 'https://a.storyblok.com/f/228091/x/75f6c49d71/informace-o-zpracovani-a-ochrane-osobnich-udaju.pdf';

	const maxUploadFiles = 3;
	const maxUploadFileSizeInMb = 5;
	const maxUploadFileSizeMb = computed(() => {
		// This code is used to insert a non-breaking space.
		const file = filesize(maxUploadFileSizeInMb * 1024 * 1024, {standard: 'jedec', output: 'array'});
		return file[0] + ' ' + file[1];
	});
	const acceptFileTypes = '.png,.jpg,.svg,.pdf,.jpeg,.HEIC';

	const {profile} = storeToRefs(useCustomerProfileStore());

	const formData = ref({
		name: profile.value?.personalInfo?.name ?? '',
		surname: profile.value?.personalInfo?.surname ?? '',
		email: profile.value?.contacts?.email ?? '',
		phone: profile.value?.contacts?.phone ?? '',
		eanGlobusCard: profile.value?.account?.card?.eanGlobusCard ?? '',
		reason: '',
		house: '',
		files: [],
		message: '',
		terms: false,
	});

	const reasons = [
		'Podnět ke zlepšení',
		'Věrnostní program Můj Globus',
		'Aplikace Můj Globus',
		'Nabídka spolupráce',
		'Pochvala',
		'Jiné',
	];

	const {branches} = storeToRefs(useStoryblokHypermarketsStore());

	const hypermarketsGsoaId = computed(() =>
		branches.value?.map((hypermarket) => ({
			label: hypermarket.content.name,
			value: hypermarket.slug,
		})),
	);

	const reasonOptions = computed(() =>
		reasons.map((item) => ({
			label: item,
			value: slugify(item.toLowerCase()),
		})),
	);

	const isSent = ref(false);
	const loading = ref(false);
	const error = ref(false);
	const errorMessage = ref('');
	const sectionEl = ref<HTMLElement | null>(null);

	const {executeRecaptcha} = useGoogleRecaptcha();

	async function submit() {
		set(loading, true);
		set(error, false);
		set(errorMessage, '');

		const {token} = await executeRecaptcha('sendContactForm');

		const formDataToSend: contactForm = {
			name: formData.value.name,
			surname: formData.value.surname,
			email: formData.value.email,
			reason: formData.value.reason,
			house: formData.value.house?.length ? formData.value.house : null,
			message: formData.value.message,
			terms: String(formData.value.terms),
			token: String(token),
		};

		if (formData.value.phone?.length) {
			formDataToSend.phone = formData.value.phone;
		}

		if (formData.value.eanGlobusCard?.length) {
			formDataToSend.eanGlobusCard = formData.value.eanGlobusCard;
		}

		if (formData.value.files.length > 0) {
			formData.value.files.forEach((file, index) => {
				if (index < maxUploadFiles && validateFileAccept(file, acceptFileTypes)) {
					formDataToSend[`file${index + 1}` as keyof contactForm] = file;
				}
			});
		}

		try {
			await cancelTimeout(() => MailingService.sendContactForm({formData: formDataToSend}));
		} catch (e: any) {
			set(loading, false);
			set(error, true);
			set(errorMessage, 'Formulář se nepodařilo odeslat');
			return;
		}

		set(loading, false);
		set(isSent, true);

		scrollToSection(sectionEl.value);
	}

	function validateMaxFiles(node: FormKitNode, maxFiles = 3) {
		const files = node.value as File[];
		return files.length <= maxFiles;
	}

	function validateMaxFileSize(node: FormKitNode, maxSizeInMb = 5) {
		const files = node.value as File[];
		const maxSizeInBytes = maxSizeInMb * 1024 * 1024;
		const totalSize = files.reduce((acc, file) => acc + file.size, 0);
		return totalSize <= maxSizeInBytes;
	}

	function validateAcceptFiles(node: FormKitNode) {
		const files = node.value as File[];
		return files.every((file) => validateFileAccept(file, acceptFileTypes));
	}
</script>
